import {inject} from '@angular/core';
import {Routes} from '@angular/router';
import {canActivatePermission} from './shared/guards/permission.guard';
import {MenuRoles} from './shell/MenuRoles';
import {canActivateAuth} from '~local/app/shared/guards/authentication.guard';
import {AuthenticationService} from '~local/app/shared/services/authentication.service';
import {map} from 'rxjs/operators';
import {unsavedConfirmGuard} from '~shared/ng/guards/unsaved-confirm.guard';
import {extract} from '~local/app/shared/services/i18n.service';

export const routes: Routes = [
    {
        path: '',
        loadComponent: () => import('./shell/shell.component'),
        canActivate: [canActivateAuth],
        children: [
            {path: '', redirectTo: 'dashboard', pathMatch: 'full'},
            {
                path: 'dashboard',
                loadComponent: () => import('app/dashboard/dashboard.component').then(c => c.DashboardComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.DASHBOARD.roleName,
                    title: 'DASHBOARD'
                }
            },
            {
                path: 'users',
                loadComponent: () => import('app/users/users.component').then(c => c.UsersComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.USERS.roleName,
                    title: 'SYSTEM_USERS'
                }
            },
            {
                path: 'users/:id',
                loadComponent: () => import('app/user-detail/user-detail.component').then(c => c.UserDetailComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.USERS.roleName,
                    title: 'USER_DETAILS'
                }
            },
            {
                path: 'transactions',
                loadComponent: () => import('app/transactions/transactions.component').then(c => c.TransactionsComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.ALL_TRANSACTIONS.roleName,
                    title: 'ALL_TRANSACTIONS'
                }
            },
            {
                path: 'transactions/:id',
                loadComponent: () => import('app/transaction/transaction.component').then(c => c.TransactionComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.ALL_TRANSACTIONS.roleName,
                    title: 'TRANSACTION'
                }
            },
            // { path: 'cards', loadChildren: 'app/cards/cards.module#CardsModule', canActivate: [canActivatePermission], data: {permission: MenuRoles.ALL_TRANSACTIONS.roleName} },
            {
                path: 'exchange',
                loadComponent: () => import('app/exchange/exchange.component').then(c => c.ExchangeComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.EXCHANGE_SERVICE.roleName,
                    title: 'EXCHANGE_SETTINGS'
                }
            },
            {path: 'about', loadComponent: () => import('app/about/about.component')},
            {
                path: 'coinapi',
                loadComponent: () => import('app/coinapi/coinapi.component').then(c => c.CoinApiComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.CRYPTO_SERVICE.roleName,
                    title: 'CRYPTO_SETTINGS'
                }
            },
            {
                path: 'triggers',
                loadComponent: () => import('app/notification-triggers/notification.triggers.component').then(c => c.NotificationTriggersComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.SETTINGS.roleName,
                    title: 'NOTIFICATION_TRIGGERS'
                }
            },
            {
                path: 'settings',
                loadComponent: () => import('app/settings/settings.component').then(c => c.SettingsComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.SETTINGS.roleName,
                    title: 'SETTINGS'
                }
            },
            {
                path: 'country/limit',
                loadComponent: () => import('app/country-limit/country-limit.component').then(c => c.CountryLimitComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.SETTINGS.roleName,
                    title: 'COUNTRY_LIMIT'
                }
            },
            {
                path: 'country/limitdetail',
                loadComponent: () => import('~local/app/country-limit-detail/country-limit-detail.component').then(c => c.CountryLimitDetailComponent),
                canActivate: [canActivatePermission],
                canDeactivate: [unsavedConfirmGuard],
                data: {
                    permission: MenuRoles.SETTINGS.roleName,
                    title: 'COUNTRY_LIMIT_DETAILS'
                }
            },
            {
                path: 'country/limitdetail/:id',
                loadComponent: () => import('~local/app/country-limit-detail/country-limit-detail.component').then(c => c.CountryLimitDetailComponent),
                canActivate: [canActivatePermission],
                canDeactivate: [unsavedConfirmGuard],
                data: {
                    permission: MenuRoles.SETTINGS.roleName,
                    title: 'COUNTRY_LIMIT_DETAILS'
                }
            },
            {
                path: 'kyc',
                loadComponent: () => import('app/kyc/kyc.component').then(c => c.KycComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.KYC.roleName,
                    title: 'KYC'
                }
            },

            {
                path: 'account-openings',
                loadComponent: () => import('app/account-openings/account-openings.component').then(c => c.AccountOpeningsComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.KYC.roleName,
                    title: 'ACCOUNT_OPENINGS'
                }
            },

            {
                path: 'inactive-kyc',
                loadComponent: () => import('app/inactive-kyc/inactive-kyc.component').then(c => c.InactiveKycComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.KYC.roleName,
                    title: 'INACTIVE_KYC'
                }
            },
            {
                path: 'bankinout',
                loadComponent: () => import('app/bank-in-out/bank-in-out.component').then(c => c.BankInOutComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.BANK_IN_OUT.roleName,
                    title: 'BANK_TRANSACTIONS'
                }
            },
            {
                path: 'bankinout/:id',
                loadComponent: () => import('app/transaction/transaction.component').then(c => c.TransactionComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.BANK_IN_OUT.roleName,
                    title: 'TRANSACTION'
                }
            },
            {
                path: 'cryptoout',
                loadComponent: () => import('app/crypto-out/crypto-out.component').then(c => c.CryptoOutComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.CRYPTO_OUT.roleName,
                    title: 'CRYPTO_TRANSACTIONS'
                }
            },
            {
                path: 'cryptoout/:id',
                loadComponent: () => import('app/transaction/transaction.component').then(c => c.TransactionComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.CRYPTO_OUT.roleName,
                    title: 'TRANSACTION'
                }
            },
            {
                path: 'private',
                loadComponent: () => import('app/private-clients/private-clients.component').then(c => c.PrivateClientsComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.CLIENTS.roleName,
                    title: 'PRIVATE_CLIENTS'
                }
            },
            {
                path: 'business',
                loadComponent: () => import('app/business-clients/business-clients.component').then(c => c.BusinessClientsComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.CLIENTS.roleName,
                    title: 'BUSINESS_CLIENTS'
                }
            },
            {
                path: 'inactive',
                loadComponent: () => import('app/inactive-clients/inactive-clients.component').then(c => c.InactiveClientsComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.CLIENTS.roleName,
                    title: 'FREEZED_DISABLED'
                }
            },
            {
                path: 'clients/:id',
                loadComponent: () => import('app/user-detail/user-detail.component').then(c => c.UserDetailComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.CLIENTS.roleName,
                    title: 'USER_DETAILS'
                }
            },
            {
                path: 'tickets',
                loadComponent: () => import('app/tickets/tickets.component').then(c => c.TicketsComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.TICKETS.roleName,
                    title: 'SUPPORT'
                }
            },
            {
                path: 'statistics',
                loadComponent: () => import('app/statistics/statistics.component').then(c => c.StatisticsComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.STATISTICS.roleName,
                    title: 'STATISTICS'
                }
            },
            {
                path: 'calendar',
                loadComponent: () => import('app/kyc-calendar/kyc-calendar.component').then(c => c.KycCalendarComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.KYC.roleName,
                    title: 'KYC_MEETINGS'
                }
            },
            {
                path: 'balance/:code',
                loadComponent: () => import('app/balance/balance.component').then(c => c.BalanceComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.BALANCE.roleName,
                    title: 'BALANCE'
                }
            },
            {
                path: 'commissions',
                loadComponent: () => import('app/commission/commission.component').then(c => c.CommissionComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.SETTINGS.roleName,
                    title: 'SERVICE_MANAGEMENT'
                }
            },
            {
                path: 'control',
                loadComponent: () => import('app/tier-limit-control/tier-limit-control.component').then(c => c.TierLimitControlComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.SETTINGS.roleName,
                    title: 'TIER_CONTROL'
                }
            },
            {
                path: 'pool',
                loadComponent: () => import('app/pool/pool.component').then(c => c.PoolComponent),
                data: {
                    permission: MenuRoles.SETTINGS.roleName,
                    title: 'POOL_MANAGEMENT'
                }
            },
            {
                path: 'package',
                loadComponent: () => import('app/package/package.component').then(c => c.PackageComponent),
                data: {
                    permission: MenuRoles.SETTINGS.roleName,
                    title: 'PACKAGE_MANAGEMENT'
                }
            },
            {
                path: 'pool/:code',
                loadComponent: () => import('app/pool-currency/pool-currency.component').then(c => c.PoolCurrencyComponent),
                data: {
                    permission: MenuRoles.SETTINGS.roleName,
                    title: 'TOKEN'
                }
            },
            {
                path: 'form/:id',
                loadComponent: () => import('app/kyc-tier-two-form/kyc-tier-two-form.component'),
                canActivate: [canActivatePermission],
                canDeactivate: [unsavedConfirmGuard],
                data: {permission: MenuRoles.CLIENTS.roleName, title: 'QUESTION_FORM'}
            },
            {
                path: 'center',
                loadComponent: () => import('app/action-center/action-center.component').then(c => c.ActionCenterComponent),
                data: {
                    permission: MenuRoles.SETTINGS.roleName,
                    title: 'ACTION_CENTER'
                }
            },
            {
                path: 'cards/transactions',
                loadComponent: () => import('app/card-transactions/card-transactions.component').then(c => c.CardTransactionsComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.ALL_TRANSACTIONS.roleName,
                    title: 'CARD_TRANSACTIONS'
                }
            },
            {
                path: 'cards/transactions/:id',
                loadComponent: () => import('app/transaction/transaction.component').then(c => c.TransactionComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.ALL_TRANSACTIONS.roleName,
                    title: 'TRANSACTION'
                }
            },
            {
                path: 'custody/transactions',
                loadComponent: () => import('app/custody-transactions/custody-transactions.component').then(c => c.CustodyTransactionsComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.ALL_TRANSACTIONS.roleName,
                    title: 'CUSTODY_TRANSACTIONS'
                }
            },
            {
                path: 'custody/transactions/:id',
                loadComponent: () => import('app/transaction/transaction.component').then(c => c.TransactionComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.ALL_TRANSACTIONS.roleName,
                    title: 'TRANSACTION'

                }
            },
            {
                path: 'cards/:id',
                loadComponent: () => import('app/client-card-form/client-card-form.component').then(c => c.ClientCardFormComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.CLIENTS.roleName,
                    title: 'CLIENT_CARD'
                }
            },
            {
                path: 'cards',
                loadComponent: () => import('app/cards/cards.component').then(c => c.CardsComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.STATISTICS.roleName,
                    title: extract('CARDS')
                }
            },
            {
                path: 'referral',
                loadComponent: () => import('app/referral-program/referral-program.component').then(c => c.ReferralComponent),
                canActivate: [canActivatePermission],
                data: {
                    permission: MenuRoles.SETTINGS.roleName,
                    title: 'REFERRAL_PROGRAM'
                }
            },

        ]
    },
    {
        path: 'login',
        loadComponent: () => import('app/login/login.component'),
        canMatch: [() => {
            return inject(AuthenticationService).isAuthenticated().pipe(map($isAuthenticated => !$isAuthenticated));
        }]
    },
    // Fallback when no prior route is matched
    {path: '**', redirectTo: '', pathMatch: 'full'}
];
